import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import type { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import {
  trendMetricsForPlatform,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewLeadingPlatform,
  type PaidMediaOverviewTrendMetrics,
  type PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewAmount,
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import { formatOverviewBucketLabel, OVERVIEW_GRID_COLOR, overviewAxisProps } from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

type TrendMetric = "spendRevenue" | "revenue" | "spend" | "purchases" | "roas" | "cpa";

interface TrendChartRow {
  label: string;
  point: PaidMediaOverviewTrendPoint;
  [seriesKey: string]: string | number | null | PaidMediaOverviewTrendPoint;
}

const METRIC_OPTIONS: Array<{ value: TrendMetric; labelKey: string; tooltipId?: string }> = [
  { value: "spendRevenue", labelKey: "paidMediaOverview.trend.metricSpendRevenue" },
  { value: "revenue", labelKey: "paidMediaOverview.metrics.revenue", tooltipId: "revenue" },
  { value: "spend", labelKey: "paidMediaOverview.metrics.spend", tooltipId: "spend" },
  {
    value: "purchases",
    labelKey: "paidMediaOverview.metrics.purchases",
    tooltipId: "paidPurchases",
  },
  { value: "roas", labelKey: "paidMediaOverview.metrics.roas", tooltipId: "roas" },
  { value: "cpa", labelKey: "paidMediaOverview.metrics.cpa", tooltipId: "costPerPurchase" },
];

const SUBTITLE_KEY: Record<PaidMediaOverviewGranularity, string> = {
  daily: "paidMediaOverview.trend.subtitleDaily",
  weekly: "paidMediaOverview.trend.subtitleWeekly",
  monthly: "paidMediaOverview.trend.subtitleMonthly",
};

const GRANULARITY_LABEL_KEY: Record<PaidMediaOverviewGranularity, string> = {
  daily: "paidMediaOverview.granularity.daily",
  weekly: "paidMediaOverview.granularity.weekly",
  monthly: "paidMediaOverview.granularity.monthly",
};

const METRIC_SERIES_LABEL_KEY: Record<Exclude<TrendMetric, "spendRevenue">, string> = {
  spend: "paidMediaOverview.trend.seriesSpendFor",
  revenue: "paidMediaOverview.trend.seriesRevenueFor",
  purchases: "paidMediaOverview.conversion.seriesPurchasesFor",
  roas: "paidMediaOverview.efficiency.seriesRoasFor",
  cpa: "paidMediaOverview.efficiency.seriesCpaFor",
};

const TREND_CHART_MARGIN = { top: 16, right: 10, bottom: 5, left: 0 };
const TREND_Y_AXIS_WIDTH = 88;

const seriesKey = (slug: string, metric: Exclude<TrendMetric, "spendRevenue">): string =>
  `${slug}${metric.charAt(0).toUpperCase()}${metric.slice(1)}`;

const metricValue = (
  metrics: PaidMediaOverviewTrendMetrics | null,
  metric: Exclude<TrendMetric, "spendRevenue">
): number | null => {
  if (!metrics) {
    return null;
  }
  switch (metric) {
    case "spend":
      return metrics.spend ?? null;
    case "revenue":
      return metrics.revenue ?? null;
    case "purchases":
      return metrics.purchases ?? null;
    case "roas":
      return metrics.roas ?? null;
    default:
      return metrics.cost_per_purchase ?? null;
  }
};

const toChartRow = (
  point: PaidMediaOverviewTrendPoint,
  granularity: PaidMediaOverviewGranularity,
  t: TFunction,
  locale: string
): TrendChartRow => {
  const row: TrendChartRow = {
    label: formatOverviewBucketLabel(
      point?.date,
      granularity,
      date => t("paidMediaOverview.trend.weekOf", { date }),
      locale
    ),
    point,
  };

  OVERVIEW_PLATFORM_DESCRIPTORS.forEach(descriptor => {
    const metrics = trendMetricsForPlatform(point, descriptor.key);
    (["spend", "revenue", "purchases", "roas", "cpa"] as const).forEach(metric => {
      row[seriesKey(descriptor.slug, metric)] = metricValue(metrics, metric);
    });
  });

  return row;
};

const TooltipColumn: FC<{
  heading: string;
  slug: string;
  metrics: PaidMediaOverviewTrendMetrics | null;
  currency: string | null;
}> = ({ heading, slug, metrics, currency }) => {
  const { t } = useTranslation();

  return (
    <div>
      <div
        className={`paid-media-overview__chart-tooltip-heading paid-media-overview__chart-tooltip-heading--${slug}`}
      >
        {heading}
      </div>
      <div>
        {t("paidMediaOverview.metrics.spend")} {formatOverviewAmount(metrics?.spend, currency)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.revenue")} {formatOverviewAmount(metrics?.revenue, currency)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.purchases")} {formatOverviewNumber(metrics?.purchases)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.roas")} {formatOverviewMultiplier(metrics?.roas)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.cpa")}{" "}
        {formatOverviewUnitCost(metrics?.cost_per_purchase, currency)}
      </div>
    </div>
  );
};

const TrendTooltip: FC<TooltipProps<ValueType, NameType> & { currency: string | null }> = ({
  active,
  payload,
  currency,
}) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload as TrendChartRow | undefined;
  if (!row?.point) {
    return null;
  }

  const leaderLabel = overviewPlatformLabel(t, row.point.leader);

  return (
    <div className="paid-media-overview__chart-tooltip">
      <div className="paid-media-overview__chart-tooltip-title">{row.label}</div>
      <div className="paid-media-overview__chart-tooltip-columns paid-media-overview__chart-tooltip-columns--quad">
        {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
          <TooltipColumn
            key={descriptor.key}
            heading={t(descriptor.labelKey)}
            slug={descriptor.slug}
            metrics={trendMetricsForPlatform(row.point, descriptor.key)}
            currency={currency}
          />
        ))}
      </div>
      {leaderLabel ? (
        <div className="paid-media-overview__chart-tooltip-footer">
          {t("paidMediaOverview.trend.tooltipLeader")} <strong>{leaderLabel}</strong>
        </div>
      ) : null}
    </div>
  );
};

interface SpendRevenueTrendCardProps {
  series: PaidMediaOverviewTrendPoint[];
  granularity: PaidMediaOverviewGranularity;
  leadingPlatform: PaidMediaOverviewLeadingPlatform | null;
  currency: string | null;
  loading: boolean;
}

const SpendRevenueTrendCard: FC<SpendRevenueTrendCardProps> = ({
  series,
  granularity,
  leadingPlatform,
  currency,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  const [metric, setMetric] = useState<TrendMetric>("spendRevenue");

  const data: TrendChartRow[] = useMemo(
    () => (series ?? []).map(point => toChartRow(point, granularity, t, i18n.language)),
    [series, granularity, t, i18n.language]
  );

  const tickFormatter = (value: number) => {
    if (metric === "roas") {
      return formatOverviewMultiplier(value, 0);
    }
    return metric === "purchases"
      ? formatOverviewNumber(value)
      : formatOverviewCurrency(value, currency);
  };

  const activeMetricLabel = t(
    METRIC_OPTIONS.find(option => option.value === metric)?.labelKey ?? ""
  );
  const leadingPlatformName = overviewPlatformLabel(t, leadingPlatform?.platform);

  const legendItems = useMemo(() => {
    if (metric === "spendRevenue") {
      return [
        ...OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => ({
          key: `${descriptor.slug}-spend`,
          label: t("paidMediaOverview.trend.seriesSpendFor", { platform: t(descriptor.labelKey) }),
          color: descriptor.color,
          isBar: true,
        })),
        ...OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => ({
          key: `${descriptor.slug}-revenue`,
          label: t("paidMediaOverview.trend.seriesRevenueFor", {
            platform: t(descriptor.labelKey),
          }),
          color: descriptor.revenueColor,
          isBar: false,
        })),
      ];
    }

    return OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => ({
      key: `${descriptor.slug}-${metric}`,
      label: t(METRIC_SERIES_LABEL_KEY[metric], { platform: t(descriptor.labelKey) }),
      color: descriptor.color,
      isBar: false,
    }));
  }, [metric, t]);

  const renderChartBody = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        {metric === "spendRevenue" ? (
          <ComposedChart data={data} margin={chartRtl.margin(TREND_CHART_MARGIN)}>
            <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
            <XAxis dataKey="label" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
            <YAxis
              yAxisId="l"
              width={TREND_Y_AXIS_WIDTH}
              orientation={chartRtl.yAxisOrientation}
              tickFormatter={value => formatOverviewCurrency(value, currency)}
              {...overviewAxisProps}
            />
            <YAxis
              yAxisId="r"
              orientation={chartRtl.oppositeYAxisOrientation}
              width={TREND_Y_AXIS_WIDTH}
              tickFormatter={value => formatOverviewCurrency(value, currency)}
              {...overviewAxisProps}
            />
            <Tooltip
              wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
              content={<TrendTooltip currency={currency} />}
            />
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <Bar
                key={`${descriptor.key}-spend`}
                yAxisId="l"
                dataKey={seriesKey(descriptor.slug, "spend")}
                name={t("paidMediaOverview.trend.seriesSpendFor", {
                  platform: t(descriptor.labelKey),
                })}
                fill={descriptor.color}
                fillOpacity={0.8}
                radius={[3, 3, 0, 0]}
              />
            ))}
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <Line
                key={`${descriptor.key}-revenue`}
                yAxisId="r"
                type="monotone"
                dataKey={seriesKey(descriptor.slug, "revenue")}
                name={t("paidMediaOverview.trend.seriesRevenueFor", {
                  platform: t(descriptor.labelKey),
                })}
                stroke={descriptor.revenueColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </ComposedChart>
        ) : (
          <LineChart data={data} margin={chartRtl.margin(TREND_CHART_MARGIN)}>
            <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
            <XAxis dataKey="label" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
            <YAxis
              width={TREND_Y_AXIS_WIDTH}
              orientation={chartRtl.yAxisOrientation}
              {...overviewAxisProps}
              tickFormatter={tickFormatter}
            />
            <Tooltip
              wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
              content={<TrendTooltip currency={currency} />}
            />
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <Line
                key={descriptor.key}
                type="monotone"
                dataKey={seriesKey(descriptor.slug, metric)}
                name={`${t(descriptor.labelKey)} ${activeMetricLabel}`}
                stroke={descriptor.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    );
  };

  return (
    <article className="paid-media-panel paid-media-overview__trend-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>{t("paidMediaOverview.trend.title")}</h3>
          <p className="paid-media-overview__panel-subtitle">{t(SUBTITLE_KEY[granularity])}</p>
        </div>
      </div>

      <div
        className="paid-media-overview__trend-metric-tabs"
        role="group"
        aria-label={t("paidMediaOverview.trend.metricTabsAria")}
      >
        {METRIC_OPTIONS.map(option => {
          const label = t(option.labelKey);
          const selected = metric === option.value;
          return (
            <div key={option.value} className="paid-media-overview__trend-metric-tab">
              <button
                type="button"
                onClick={() => setMetric(option.value)}
                aria-pressed={selected}
                className={
                  selected
                    ? "paid-media-overview__trend-metric-btn paid-media-overview__trend-metric-btn--active"
                    : "paid-media-overview__trend-metric-btn"
                }
              >
                {label}
              </button>
              {option.tooltipId ? (
                <OverviewMetricTip id={option.tooltipId} label={label} />
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="paid-media-overview__chart paid-media-overview__chart--trend">
        {renderChartBody()}
      </div>

      <div
        className="paid-media-overview__chart-legend-row"
        aria-label={t("paidMediaOverview.trend.legendAria")}
      >
        {legendItems.map(item => (
          <div key={item.key} className="paid-media-overview__chart-legend-item">
            {item.isBar ? (
              <LegendSwatchIcon color={item.color} />
            ) : (
              <LegendLineIcon color={item.color} />
            )}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {leadingPlatformName ? (
        <div className="paid-media-overview__callout">
          <strong>
            {t("paidMediaOverview.trend.calloutLabel", {
              period: t(GRANULARITY_LABEL_KEY[granularity]),
            })}
          </strong>{" "}
          {t("paidMediaOverview.trend.calloutLeader", {
            platform: leadingPlatformName,
            roas: formatOverviewMultiplier(leadingPlatform?.roas),
          })}
        </div>
      ) : null}
    </article>
  );
};

const LegendLineIcon: FC<{ color: string }> = ({ color }) => (
  <svg width="32" height="14" viewBox="0 0 32 14" fill="none" aria-hidden="true">
    <path
      strokeWidth="4"
      fill="none"
      stroke={color}
      d="M0,7 H10.67 A5.33,5.33,0,1,1,21.33,7 H32 M21.33,7 A5.33,5.33,0,1,1,10.67,7"
      className="recharts-legend-icon"
    />
  </svg>
);

const LegendSwatchIcon: FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <rect width="12" height="12" rx="3" fill={color} />
  </svg>
);

export default SpendRevenueTrendCard;
